import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  DollarSign,
  Plus,
  MoreHorizontal,
  FileText,
  Check,
  AlertTriangle,
  Clock,
  Edit2,
  Trash2,
  Copy,
  Send,
  Link2,
  Ban,
  GripVertical,
  Loader2,
  Calendar,
  Percent,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  History,
  RotateCcw,
  PackageCheck,
  Undo2,
  CheckCircle2,
  ArrowRight,
  Mail
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Progress } from "../ui/progress";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import SubscriptionRow, { AddSubscriptionRow } from "./SubscriptionRow";
// Collections, dunning and the pause. Moved here from the Client Tracker
// page: it is finance-only, and it belongs next to the money it is about.
import TrackerCollectionsCard from "./TrackerCollectionsCard";

// Status colors
const STATUS_COLORS = {
  Scheduled: "bg-slate-100 text-slate-700",
  SubmittedToFinance: "bg-blue-100 text-blue-700",
  InvoiceRequested: "bg-blue-100 text-blue-700",
  InvoiceSent: "bg-indigo-100 text-indigo-700",
  Invoiced: "bg-indigo-100 text-indigo-700",
  PartiallyPaid: "bg-amber-100 text-amber-700",
  Paid: "bg-green-100 text-green-700",
  Overdue: "bg-red-100 text-red-700",
  Returned: "bg-orange-100 text-orange-700",
  Waived: "bg-zinc-100 text-zinc-500"
};

const STATUS_LABELS = {
  Scheduled: "Scheduled",
  SubmittedToFinance: "Submitted to Finance",
  InvoiceRequested: "Invoice Requested",
  InvoiceSent: "Invoice Sent",
  Invoiced: "Invoiced",
  PartiallyPaid: "Partially Paid",
  Paid: "Paid",
  Overdue: "Overdue",
  Returned: "Returned to PM",
  Waived: "Waived"
};

// PM-friendly status labels (hides finance details)
const PM_STATUS_LABELS = {
  Scheduled: "Pending",
  SubmittedToFinance: "Submitted",
  InvoiceRequested: "In Progress",
  InvoiceSent: "In Progress",
  Invoiced: "In Progress",
  PartiallyPaid: "In Progress",
  Paid: "Completed",
  Overdue: "Needs Attention",
  Returned: "Action Required",
  Waived: "Waived"
};

const PM_STATUS_COLORS = {
  Scheduled: "bg-slate-100 text-slate-700",
  SubmittedToFinance: "bg-blue-100 text-blue-700",
  InvoiceRequested: "bg-blue-100 text-blue-700",
  InvoiceSent: "bg-blue-100 text-blue-700",
  Invoiced: "bg-blue-100 text-blue-700",
  PartiallyPaid: "bg-blue-100 text-blue-700",
  Paid: "bg-green-100 text-green-700",
  Overdue: "bg-amber-100 text-amber-700",
  Returned: "bg-orange-100 text-orange-700",
  Waived: "bg-zinc-100 text-zinc-500"
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return dateString;
  }
};

// Due rule labels
const DUE_RULE_LABELS = {
  OnActivation: "On Activation",
  OnStartDate: "On Start Date",
  OnMilestoneCompletion: "On Milestone Completion",
  OnDeliverableCount: "On Deliverable Progress",
  ManualDate: "Manual Date",
  RelativeDaysFromStart: "Days from Start"
};

// Renders standalone as the Payment Schedule tab, and embedded inside the
// Planning popup. When embedded: `onChanged` pushes each fresh schedule payload
// up so the popup's timeline milestone row tracks edits made here, and bumping
// `refreshToken` forces a refetch after the popup changes payments elsewhere
// (e.g. the scope→timeline apply flow syncing the schedule).
const MilestonePaymentsTab = ({ programId, onChanged, refreshToken }) => {
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [program, setProgram] = useState(null);
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState({});
  const [isFinance, setIsFinance] = useState(false);
  // { subscription, periods, summary, stripe_configured, suggested_from_quote }
  const [subscription, setSubscription] = useState(null);

  // Dialog states
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showEditMilestone, setShowEditMilestone] = useState(false);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [showWaive, setShowWaive] = useState(false);
  const [showAttachInvoice, setShowAttachInvoice] = useState(false);
  const [showEditSchedule, setShowEditSchedule] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSubmitDelivery, setShowSubmitDelivery] = useState(false);
  const [showReturnToPm, setShowReturnToPm] = useState(false);
  const [showMarkInvoiceSent, setShowMarkInvoiceSent] = useState(false);

  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    title: "",
    type: "Custom",
    amount_type: "Fixed",
    amount_value: 0,
    due_rule_type: "ManualDate",
    due_date: "",
    trigger_threshold: 50,
    relative_days: 14,
    payment_method_expected: "Unknown",
    notes: ""
  });
  const [paidForm, setPaidForm] = useState({ paid_amount: 0, paid_date: "", notes: "" });
  const [waiveReason, setWaiveReason] = useState("");
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [scheduleForm, setScheduleForm] = useState({ contract_value: 0, currency: "USD" });
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deliveryForm, setDeliveryForm] = useState({ delivery_note: "", delivery_proof_url: "" });
  const [returnReason, setReturnReason] = useState("");
  const [invoiceSentForm, setInvoiceSentForm] = useState({ invoice_url: "", invoice_id: "" });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      console.log("[MilestonePayments] Fetching data for program:", programId);
      
      // The subscription is its own ledger on its own endpoint — a project can
      // have one with no payment schedule at all, so a failure there must not
      // take the milestones down with it.
      const [scheduleRes, templatesRes, subRes] = await Promise.all([
        apiClient.get(`/programs/${programId}/payments/schedule`),
        apiClient.get("/payments/templates"),
        apiClient
          .get(`/programs/${programId}/subscription`)
          .catch(() => ({ data: null })),
      ]);

      console.log("[MilestonePayments] Schedule response:", scheduleRes.data);

      setSchedule(scheduleRes.data.schedule);
      setMilestones(scheduleRes.data.milestones || []);
      setProgram(scheduleRes.data.program);
      setSummary(scheduleRes.data.summary);
      setTemplates(templatesRes.data);
      setIsFinance(scheduleRes.data.user_is_finance ?? false);
      setSubscription(subRes.data);
      onChanged?.(scheduleRes.data);
    } catch (error) {
      console.error("[MilestonePayments] Error fetching data:", error);
      console.error("[MilestonePayments] Response:", error.response?.data);
      if (error.response?.status === 403) {
        toast.error("Access denied - requires Admin/COO/Accountant role");
      } else if (error.response?.status === 404) {
        toast.error("Program not found");
      } else {
        toast.error("Failed to load payment data. Check console for details.");
      }
    } finally {
      setLoading(false);
    }
  }, [programId, onChanged]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshToken]);

  // Create schedule
  const handleCreateSchedule = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/programs/${programId}/payments/schedule`, {
        template_id: selectedTemplate || null,
        contract_value: program?.contract_value || 0
      });
      toast.success("Payment schedule created");
      setShowCreateSchedule(false);
      setSelectedTemplate("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create schedule");
    } finally {
      setSaving(false);
    }
  };

  // Add milestone
  const handleAddMilestone = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/programs/${programId}/payments/milestones`, milestoneForm);
      toast.success("Milestone added");
      setShowAddMilestone(false);
      resetMilestoneForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add milestone");
    } finally {
      setSaving(false);
    }
  };

  // Edit milestone
  const handleEditMilestone = async () => {
    if (!selectedMilestone) return;
    setSaving(true);
    try {
      await apiClient.patch(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}`,
        milestoneForm
      );
      toast.success("Milestone updated");
      setShowEditMilestone(false);
      setSelectedMilestone(null);
      resetMilestoneForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update milestone");
    } finally {
      setSaving(false);
    }
  };

  // Delete milestone
  const handleDeleteMilestone = async (milestone, force = false) => {
    try {
      const url = `/programs/${programId}/payments/milestones/${milestone.id}${force ? '?force=true' : ''}`;
      await apiClient.delete(url);
      toast.success("Milestone deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete milestone";
      // If error is about paid status, ask to force delete
      if (errorMsg.includes("has payments") && !force) {
        if (confirm(`This milestone has payments recorded. Are you SURE you want to permanently delete it? This cannot be undone.`)) {
          handleDeleteMilestone(milestone, true);
        }
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Request invoice
  const handleRequestInvoice = async (milestone) => {
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${milestone.id}/request-invoice`,
        { notes: "" }
      );
      toast.success("Invoice requested - notification sent to accounting");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request invoice");
    }
  };

  // Mark paid
  const handleMarkPaid = async () => {
    if (!selectedMilestone) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/mark-paid`,
        paidForm
      );
      toast.success("Payment recorded");
      setShowMarkPaid(false);
      setSelectedMilestone(null);
      setPaidForm({ paid_amount: 0, paid_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  // Waive milestone
  const handleWaive = async () => {
    if (!selectedMilestone || !waiveReason.trim()) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/waive`,
        { reason: waiveReason }
      );
      toast.success("Milestone waived");
      setShowWaive(false);
      setSelectedMilestone(null);
      setWaiveReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to waive milestone");
    } finally {
      setSaving(false);
    }
  };

  // Reset milestone status (undo auto-sync)
  const handleResetStatus = async (milestone) => {
    if (!milestone) return;
    
    if (!confirm(`Reset "${milestone.title}" to Scheduled status with $0 paid? This will undo any auto-sync.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const res = await apiClient.post(
        `/programs/${programId}/payments/milestones/${milestone.id}/reset-status`
      );
      toast.success(`Milestone reset from ${res.data.old_status} to Scheduled`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset milestone");
    } finally {
      setSaving(false);
    }
  };

  // Attach invoice
  const handleAttachInvoice = async () => {
    if (!selectedMilestone || !invoiceUrl.trim()) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/attach-invoice`,
        { invoice_url: invoiceUrl, invoice_id: invoiceId || null }
      );
      toast.success("Invoice attached");
      setShowAttachInvoice(false);
      setSelectedMilestone(null);
      setInvoiceUrl("");
      setInvoiceId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to attach invoice");
    } finally {
      setSaving(false);
    }
  };

  // Duplicate milestone
  const handleDuplicate = async (milestone) => {
    try {
      await apiClient.post(`/programs/${programId}/payments/milestones/${milestone.id}/duplicate`);
      toast.success("Milestone duplicated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate");
    }
  };

  // Update schedule
  const handleUpdateSchedule = async () => {
    setSaving(true);
    try {
      const res = await apiClient.patch(`/programs/${programId}/payments/schedule`, scheduleForm);
      if (res.data.warnings?.length > 0) {
        toast.warning(`Updated with warnings: ${res.data.warnings.length} invoiced milestones affected`);
      } else {
        toast.success("Schedule updated");
      }
      setShowEditSchedule(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update schedule");
    } finally {
      setSaving(false);
    }
  };

  // Fetch logs
  const fetchLogs = async (milestone) => {
    try {
      const res = await apiClient.get(`/programs/${programId}/payments/milestones/${milestone.id}/logs`);
      setLogs(res.data);
      setSelectedMilestone(milestone);
      setShowLogs(true);
    } catch (error) {
      toast.error("Failed to fetch logs");
    }
  };

  // Bulk initialize all payment schedules (admin function)
  const handleBulkInit = async () => {
    setSaving(true);
    try {
      console.log("[MilestonePayments] Running bulk initialize...");
      const res = await apiClient.post("/payments/bulk-initialize");
      console.log("[MilestonePayments] Bulk init response:", res.data);
      
      const created = res.data.created?.length || 0;
      const existing = res.data.already_exists?.length || 0;
      const errors = res.data.errors?.length || 0;
      
      if (created > 0) {
        toast.success(`Initialized ${created} new payment schedules`);
        fetchData();
      } else if (existing > 0) {
        toast.info(`All ${existing} projects already have payment schedules`);
        fetchData(); // Refresh anyway
      } else if (errors > 0) {
        toast.warning(`${errors} projects couldn't be initialized (missing contract value)`);
      }
    } catch (error) {
      console.error("[MilestonePayments] Bulk init error:", error);
      toast.error(error.response?.data?.detail || "Failed to initialize schedules. Check console.");
    } finally {
      setSaving(false);
    }
  };

  // Sync cash_collected from program
  const handleSyncCashCollected = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post(`/programs/${programId}/payments/sync-cash-collected`);
      if (res.data.synced_amount > 0) {
        toast.success(`Synced $${res.data.synced_amount.toLocaleString()} from program cash_collected`);
      } else {
        toast.info(res.data.message || "Already in sync");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync");
    } finally {
      setSaving(false);
    }
  };

  // Delete entire payment schedule
  const [showDeleteSchedule, setShowDeleteSchedule] = useState(false);
  
  const handleDeleteSchedule = async () => {
    setSaving(true);
    try {
      const res = await apiClient.delete(`/programs/${programId}/payments/schedule`);
      toast.success(res.data.message || "Payment schedule deleted");
      setShowDeleteSchedule(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete schedule");
    } finally {
      setSaving(false);
    }
  };

  // Submit delivery (PM action)
  const handleSubmitDelivery = async () => {
    if (!selectedMilestone) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/submit-delivery`,
        deliveryForm
      );
      toast.success("Milestone submitted to Finance for invoicing");
      setShowSubmitDelivery(false);
      setSelectedMilestone(null);
      setDeliveryForm({ delivery_note: "", delivery_proof_url: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit delivery");
    } finally {
      setSaving(false);
    }
  };

  // Return to PM (Finance action)
  const handleReturnToPm = async () => {
    if (!selectedMilestone || !returnReason.trim()) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/return-to-pm`,
        { reason: returnReason }
      );
      toast.success("Milestone returned to PM");
      setShowReturnToPm(false);
      setSelectedMilestone(null);
      setReturnReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to return milestone");
    } finally {
      setSaving(false);
    }
  };

  // Mark invoice sent (Finance action)
  const handleMarkInvoiceSent = async () => {
    if (!selectedMilestone) return;
    setSaving(true);
    try {
      await apiClient.post(
        `/programs/${programId}/payments/milestones/${selectedMilestone.id}/mark-invoice-sent`,
        invoiceSentForm
      );
      toast.success("Milestone marked as Invoice Sent");
      setShowMarkInvoiceSent(false);
      setSelectedMilestone(null);
      setInvoiceSentForm({ invoice_url: "", invoice_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark invoice sent");
    } finally {
      setSaving(false);
    }
  };

  // Parse agreement for payment terms
  const [parsedMilestones, setParsedMilestones] = useState([]);
  const [showParsedConfirm, setShowParsedConfirm] = useState(false);
  const [parsing, setParsing] = useState(false);

  const handleParseAgreement = async () => {
    setParsing(true);
    try {
      const res = await apiClient.post(`/programs/${programId}/payments/parse-agreement`);
      if (res.data.parsed_milestones?.length > 0) {
        setParsedMilestones(res.data.parsed_milestones);
        setShowParsedConfirm(true);
        if (res.data.success) {
          toast.success("Payment terms extracted from agreement");
        } else {
          toast.info(res.data.message || "Showing suggested schedule based on program data");
        }
      } else {
        toast.warning("No payment terms found in agreement");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("No agreement uploaded")) {
        toast.error("No agreement uploaded. Please upload an agreement first.");
      } else {
        toast.error(detail || "Failed to parse agreement");
      }
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmParsedSchedule = async () => {
    setSaving(true);
    try {
      // Filter out milestones marked as needs_confirmation unless user kept them
      const milestonesToSave = parsedMilestones.filter(m => !m.removed);
      
      const res = await apiClient.post(`/programs/${programId}/payments/confirm-parsed`, milestonesToSave);
      toast.success(`Created ${res.data.milestones?.length || 0} payment milestones`);
      setShowParsedConfirm(false);
      setParsedMilestones([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save payment schedule");
    } finally {
      setSaving(false);
    }
  };

  const updateParsedMilestone = (index, field, value) => {
    setParsedMilestones(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeParsedMilestone = (index) => {
    setParsedMilestones(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], removed: true };
      return updated;
    });
  };

  // Reset form
  const resetMilestoneForm = () => {
    setMilestoneForm({
      title: "",
      type: "Custom",
      amount_type: "Fixed",
      amount_value: 0,
      due_rule_type: "ManualDate",
      due_date: "",
      trigger_threshold: 50,
      relative_days: 14,
      payment_method_expected: "Unknown",
      notes: ""
    });
  };

  // Open edit dialog
  const openEditDialog = (milestone) => {
    setSelectedMilestone(milestone);
    setMilestoneForm({
      title: milestone.title,
      type: milestone.type,
      amount_type: milestone.amount_type,
      amount_value: milestone.amount_value,
      due_rule_type: milestone.due_rule_type,
      due_date: milestone.due_date?.split("T")[0] || "",
      trigger_threshold: milestone.trigger_threshold || 50,
      relative_days: milestone.relative_days || 14,
      payment_method_expected: milestone.payment_method_expected || "Unknown",
      notes: milestone.notes || ""
    });
    setShowEditMilestone(true);
  };

  // Open mark paid dialog
  const openMarkPaidDialog = (milestone) => {
    setSelectedMilestone(milestone);
    setPaidForm({
      paid_amount: (milestone.calculated_amount || 0) - (milestone.paid_amount || 0),
      paid_date: new Date().toISOString().split("T")[0],
      notes: ""
    });
    setShowMarkPaid(true);
  };

  // Open edit schedule dialog
  const openEditScheduleDialog = () => {
    setScheduleForm({
      contract_value: schedule?.contract_value || program?.contract_value || 0,
      currency: schedule?.currency || "USD"
    });
    setShowEditSchedule(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No schedule yet - show creation UI
  if (!schedule) {
    return (
      <div className="space-y-6">
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Payment Schedule</h3>
            {isFinance ? (
              <>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  Create a payment schedule to track deposits, progress payments, and final balances.
                </p>
                
                {/* Show current collected status */}
                {program?.cash_collected > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 max-w-sm mx-auto">
                    <p className="text-sm text-green-800">
                      <strong>${program.cash_collected.toLocaleString()}</strong> collected 
                      ({program.contract_value > 0 ? Math.round((program.cash_collected / program.contract_value) * 100) : 0}% of contract)
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3 justify-center flex-wrap">
                  <Button 
                    variant="default"
                    onClick={handleParseAgreement} 
                    disabled={parsing}
                    data-testid="parse-agreement-btn"
                  >
                    {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Parse from Agreement
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateSchedule(true)} data-testid="create-schedule-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Manually
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground mt-4">
                  Parse from Agreement extracts payment terms from your uploaded contract.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                No payment schedule has been set up for this project yet. Finance will configure the milestone payment schedule.
              </p>
            )}
          </CardContent>
        </Card>

        {/* A platform-only VEXOS deal has a subscription and no milestone
            schedule at all, so the subscription can't live behind the schedule
            check. Same row, rendered in its own small table. */}
        {(subscription?.subscription || (isFinance && subscription?.suggested_from_quote)) && (
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="text-base">Recurring</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {subscription?.subscription ? (
                      <SubscriptionRow
                        programId={programId}
                        data={subscription}
                        isFinance={isFinance}
                        colSpan={isFinance ? 8 : 5}
                        onChanged={(fresh) => (fresh ? setSubscription(fresh) : fetchData())}
                      />
                    ) : (
                      <AddSubscriptionRow
                        programId={programId}
                        suggested={subscription?.suggested_from_quote}
                        colSpan={isFinance ? 8 : 5}
                        onChanged={(fresh) => (fresh ? setSubscription(fresh) : fetchData())}
                      />
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parsed Milestones Confirmation Dialog */}
        <Dialog open={showParsedConfirm} onOpenChange={setShowParsedConfirm}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Confirm Payment Schedule
              </DialogTitle>
              <DialogDescription>
                Review and edit the extracted payment terms before saving.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p><strong>Contract Value:</strong> {formatCurrency(program?.contract_value)}</p>
                <p><strong>Cash Collected:</strong> {formatCurrency(program?.cash_collected)}</p>
              </div>
              
              {parsedMilestones.filter(m => !m.removed).map((milestone, index) => (
                <div 
                  key={index} 
                  className={`p-4 border rounded-lg ${milestone.needs_confirmation ? 'border-amber-300 bg-amber-50/50' : 'border-border'}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={milestone.title}
                          onChange={(e) => updateParsedMilestone(index, 'title', e.target.value)}
                          className="font-medium"
                          placeholder="Milestone title"
                        />
                        {milestone.needs_confirmation && (
                          <Badge variant="outline" className="text-amber-600 border-amber-400 text-xs whitespace-nowrap">
                            Suggested
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Amount</Label>
                          <div className="flex items-center gap-1">
                            <span className="text-sm">$</span>
                            <Input
                              type="number"
                              value={milestone.calculated_amount || milestone.amount_value || 0}
                              onChange={(e) => updateParsedMilestone(index, 'calculated_amount', parseFloat(e.target.value) || 0)}
                              className="h-8"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">% of Contract</Label>
                          <p className="text-sm font-medium mt-1">
                            {milestone.percent_of_contract || (program?.contract_value > 0 ? Math.round(((milestone.calculated_amount || 0) / program.contract_value) * 100) : 0)}%
                          </p>
                        </div>
                      </div>
                      
                      {milestone.source_excerpt && (
                        <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                          Source: {milestone.source_excerpt}
                        </p>
                      )}
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeParsedMilestone(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              
              {parsedMilestones.filter(m => !m.removed).length === 0 && (
                <p className="text-center text-muted-foreground py-4">No milestones to save</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowParsedConfirm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmParsedSchedule} 
                disabled={saving || parsedMilestones.filter(m => !m.removed).length === 0}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm & Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Schedule Dialog */}
        <Dialog open={showCreateSchedule} onOpenChange={setShowCreateSchedule}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Payment Schedule</DialogTitle>
              <DialogDescription>
                Choose a template to get started quickly, or create a custom schedule.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Contract Value</Label>
                <p className="text-2xl font-light">{formatCurrency(program?.contract_value)}</p>
              </div>
              <div className="space-y-2">
                <Label>Select Template</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(templates).map(([key, template]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedTemplate(key)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedTemplate === key
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <p className="font-medium text-sm">{template.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {template.milestones.length === 0
                          ? "Start from scratch"
                          : `${template.milestones.length} milestone${template.milestones.length > 1 ? "s" : ""}`}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateSchedule(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateSchedule} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Calculate progress - use actual cash_collected vs contract_value
  const contractValue = program?.contract_value || schedule?.contract_value || 0;
  const cashCollected = program?.cash_collected || schedule?.cash_collected || 0;
  const collectionProgress = contractValue > 0
    ? (cashCollected / contractValue) * 100
    : 0;
  const milestoneProgress = (summary?.total_milestones || 0) > 0
    ? ((summary?.completed_milestones || 0) / summary.total_milestones) * 100
    : 0;
  // #, Milestone, Due, Status, actions — plus Amount, Paid and Invoice for
  // finance. Full-width rows (empty state, subscription months) span it.
  const tableColSpan = isFinance ? 8 : 5;

  return (
    <div className="space-y-6" data-testid="milestone-payments-tab">
      {/* Contract Summary Bar */}
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                {isFinance ? <DollarSign className="w-5 h-5 text-primary" /> : <CheckCircle2 className="w-5 h-5 text-primary" />}
              </div>
              <div>
                <h3 className="font-semibold" data-testid="payment-schedule-title">
                  {isFinance ? "Payment Schedule" : "Milestone Progress"}
                </h3>
                <Badge variant="outline" className={
                  schedule.schedule_status === "Active" ? "border-green-500 text-green-600" :
                  schedule.schedule_status === "Draft" ? "border-amber-500 text-amber-600" :
                  "border-zinc-500 text-zinc-600"
                }>
                  {schedule.schedule_status}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
              {isFinance && (
                <>
                  {program?.cash_collected > 0 && summary?.total_paid < program.cash_collected && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleSyncCashCollected}
                      disabled={saving}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      title="Sync cash_collected from program to milestones"
                      data-testid="sync-cash-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                      Sync
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={openEditScheduleDialog} data-testid="edit-schedule-btn">
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowDeleteSchedule(true)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="delete-schedule-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Sync notice - finance only */}
          {isFinance && program?.cash_collected > 0 && Math.abs((program.cash_collected || 0) - (summary?.total_paid || 0)) > 0.01 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <span>
                  <strong>{formatCurrency(program.cash_collected)}</strong> collected (the program record is the source of truth).
                  Milestones marked paid total <strong>{formatCurrency(summary?.total_paid)}</strong> — reconcile milestone statuses to match.
                  {program.cash_collected > (summary?.total_paid || 0) && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-blue-700 p-0 h-auto ml-1"
                      onClick={handleSyncCashCollected}
                      disabled={saving}
                    >
                      Click to sync
                    </Button>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Finance summary: shows dollar amounts */}
          {isFinance ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div data-testid="contract-value-card">
                <p className="text-xs text-muted-foreground mb-1">Contract Value</p>
                <p className="text-xl font-light">{formatCurrency(schedule.contract_value || program?.contract_value)}</p>
                {/* The subscription is a separate agreement on a separate
                    ledger. Saying so here is the difference between "our
                    numbers don't add up" and "these are two deals". */}
                {subscription?.subscription && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    One-time scope only — subscription tracked separately
                  </p>
                )}
              </div>
              <div data-testid="collected-card">
                <p className="text-xs text-muted-foreground mb-1">Collected</p>
                {/* Authoritative figure is the program's stored cash_collected,
                    not the sum of milestones marked paid (which can drift). */}
                <p className="text-xl font-light text-green-600">{formatCurrency(cashCollected)}</p>
                {Math.abs((cashCollected || 0) - (summary?.total_paid || 0)) > 0.01 && (
                  <p className="text-[11px] text-muted-foreground mt-0.5">Milestones marked paid: {formatCurrency(summary?.total_paid)}</p>
                )}
              </div>
              <div data-testid="outstanding-card">
                <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
                <p className="text-xl font-light text-orange-600">{formatCurrency(summary?.total_outstanding)}</p>
              </div>
              <div data-testid="next-payment-card">
                <p className="text-xs text-muted-foreground mb-1">Next Payment</p>
                {summary?.next_payment ? (
                  <div>
                    <p className="text-xl font-light">{formatCurrency(summary.next_payment.amount)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(summary.next_payment.due_date)}</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">—</p>
                )}
              </div>
            </div>
          ) : (
            /* PM summary: shows milestone progress, no dollar amounts */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div data-testid="total-milestones-card">
                <p className="text-xs text-muted-foreground mb-1">Total Milestones</p>
                <p className="text-xl font-light">{summary?.total_milestones || 0}</p>
              </div>
              <div data-testid="completed-milestones-card">
                <p className="text-xs text-muted-foreground mb-1">Completed</p>
                <p className="text-xl font-light text-green-600">{summary?.completed_milestones || 0}</p>
              </div>
              <div data-testid="submitted-milestones-card">
                <p className="text-xs text-muted-foreground mb-1">Submitted to Finance</p>
                <p className="text-xl font-light text-blue-600">{summary?.submitted_milestones || 0}</p>
              </div>
              <div data-testid="remaining-milestones-card">
                <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                <p className="text-xl font-light text-orange-600">
                  {(summary?.total_milestones || 0) - (summary?.completed_milestones || 0)}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">
                {isFinance ? "Collection Progress" : "Milestone Progress"}
              </span>
              <span className="text-xs font-medium">
                {isFinance 
                  ? `${Math.round(collectionProgress)}%`
                  : `${summary?.completed_milestones || 0} of ${summary?.total_milestones || 0}`
                }
              </span>
            </div>
            <Progress value={isFinance ? collectionProgress : milestoneProgress} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Milestones Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base">
            {isFinance ? "Payment Milestones" : "Milestones"}
          </CardTitle>
          {isFinance && (
            <Button size="sm" onClick={() => setShowAddMilestone(true)} data-testid="add-milestone-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Milestone
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {/* A VEXOS project can carry a subscription and no milestones at all
              (platform-only deals), so the table renders for either one. */}
          {milestones.length === 0 && !subscription?.subscription && !isFinance ? (
            <div className="p-8 text-center text-muted-foreground">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No milestones yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground w-8">#</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Milestone</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground">Due</th>
                    {isFinance && (
                      <>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-3 text-xs font-medium text-muted-foreground">Paid</th>
                      </>
                    )}
                    <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                    {isFinance && (
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Invoice</th>
                    )}
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((milestone, index) => (
                    <tr 
                      key={milestone.id} 
                      className={`border-b border-border hover:bg-muted/20 transition-colors ${
                        milestone.status === "Returned" ? "bg-orange-50/50" : ""
                      }`}
                      data-testid={`milestone-row-${milestone.id}`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {isFinance && <GripVertical className="w-4 h-4 text-muted-foreground/30 cursor-grab" />}
                          <span className="text-sm text-muted-foreground">{index + 1}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-sm flex items-center gap-1.5">
                            {milestone.title}
                            {/* Where this row came from. The ArchViz Planner pushes its
                                payments into this same schedule, so without a marker
                                there is no way to tell which rows a PM is tracking
                                there. "Detached" = a leftover the planner no longer
                                tracks; safe to remove if it duplicates another row. */}
                            {milestone.origin === "archviz-planner" && (
                              <span
                                title="Also tracked in the ArchViz Planner. Amounts and paid status set here win."
                                className="text-[9px] font-mono font-bold px-1 py-px rounded bg-green-500/15 text-green-600 dark:text-green-400"
                              >
                                AV
                              </span>
                            )}
                            {milestone.origin === "archviz-planner-detached" && (
                              <span
                                title="Left over from an earlier ArchViz Planner contract parse — the planner no longer tracks it. Check whether it duplicates another milestone here, and remove it if so."
                                className="text-[9px] font-mono font-bold px-1 py-px rounded bg-amber-500/15 text-amber-600 dark:text-amber-400"
                              >
                                AV?
                              </span>
                            )}
                          </p>
                          {isFinance ? (
                            <p className="text-xs text-muted-foreground">
                              {milestone.type} {milestone.amount_type === "PercentOfContract" 
                                ? `${milestone.amount_value}%` 
                                : "Fixed"}
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">{milestone.type}</p>
                          )}
                          {milestone.trigger_met !== undefined && (
                            <span className={`text-xs ${milestone.trigger_met ? "text-green-600" : "text-amber-600"}`}>
                              {milestone.trigger_met ? "Trigger met" : "Waiting for trigger..."}
                            </span>
                          )}
                          {/* Show return reason for PM */}
                          {!isFinance && milestone.status === "Returned" && milestone.returned_to_pm_reason && (
                            <p className="text-xs text-orange-600 mt-1">
                              Returned: {milestone.returned_to_pm_reason}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm">{formatDate(milestone.due_date)}</p>
                          {isFinance && (
                            <p className="text-xs text-muted-foreground">
                              {DUE_RULE_LABELS[milestone.due_rule_type] || milestone.due_rule_type}
                            </p>
                          )}
                        </div>
                      </td>
                      {isFinance && (
                        <>
                          <td className="p-3 text-right">
                            <p className="font-medium">{formatCurrency(milestone.calculated_amount)}</p>
                          </td>
                          <td className="p-3 text-right">
                            <p className={milestone.paid_amount > 0 ? "text-green-600 font-medium" : "text-muted-foreground"}>
                              {formatCurrency(milestone.paid_amount || 0)}
                            </p>
                          </td>
                        </>
                      )}
                      <td className="p-3 text-center">
                        <Badge 
                          className={`${isFinance ? STATUS_COLORS[milestone.status] : PM_STATUS_COLORS[milestone.status]} text-xs`}
                          data-testid={`milestone-status-${milestone.id}`}
                        >
                          {isFinance ? STATUS_LABELS[milestone.status] : PM_STATUS_LABELS[milestone.status]}
                        </Badge>
                      </td>
                      {isFinance && (
                        <td className="p-3 text-center">
                          {milestone.invoice_url ? (
                            <a
                              href={milestone.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      )}
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`milestone-menu-${milestone.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* ========= Submit Delivery (All users) ========= */}
                            {(milestone.status === "Scheduled" || milestone.status === "Returned") && (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedMilestone(milestone);
                                  setDeliveryForm({ delivery_note: "", delivery_proof_url: "" });
                                  setShowSubmitDelivery(true);
                                }}
                                data-testid={`submit-delivery-${milestone.id}`}
                              >
                                <PackageCheck className="w-4 h-4 mr-2" />
                                Submit as Delivered to Client
                              </DropdownMenuItem>
                            )}
                            {/* ========= PM-only extras ========= */}
                            {!isFinance && (
                              <DropdownMenuItem onClick={() => fetchLogs(milestone)}>
                                <History className="w-4 h-4 mr-2" />
                                View History
                              </DropdownMenuItem>
                            )}
                            {/* ========= Finance Actions ========= */}
                            {isFinance && (
                              <>
                                {(milestone.status === "Scheduled" || milestone.status === "Returned") && <DropdownMenuSeparator />}
                                <DropdownMenuItem onClick={() => openEditDialog(milestone)}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {milestone.status === "SubmittedToFinance" && (
                                  <>
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedMilestone(milestone);
                                      setInvoiceSentForm({ invoice_url: "", invoice_id: "" });
                                      setShowMarkInvoiceSent(true);
                                    }}>
                                      <Mail className="w-4 h-4 mr-2" />
                                      Mark Invoice Sent
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedMilestone(milestone);
                                      setReturnReason("");
                                      setShowReturnToPm(true);
                                    }}>
                                      <Undo2 className="w-4 h-4 mr-2" />
                                      Return to PM
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {milestone.status !== "Paid" && milestone.status !== "Waived" && (
                                  <>
                                    {milestone.status !== "SubmittedToFinance" && (
                                      <DropdownMenuItem onClick={() => handleRequestInvoice(milestone)}>
                                        <Send className="w-4 h-4 mr-2" />
                                        Request Invoice
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedMilestone(milestone);
                                      setShowAttachInvoice(true);
                                    }}>
                                      <Link2 className="w-4 h-4 mr-2" />
                                      Attach Invoice
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openMarkPaidDialog(milestone)}>
                                      <Check className="w-4 h-4 mr-2" />
                                      Mark Paid
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {(milestone.status === "InvoiceSent" || milestone.status === "Overdue") && (
                                      <DropdownMenuItem onClick={() => {
                                        setSelectedMilestone(milestone);
                                        setReturnReason("");
                                        setShowReturnToPm(true);
                                      }}>
                                        <Undo2 className="w-4 h-4 mr-2" />
                                        Return to PM
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedMilestone(milestone);
                                      setShowWaive(true);
                                    }}>
                                      <Ban className="w-4 h-4 mr-2" />
                                      Waive
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {(milestone.status === "Paid" || milestone.status === "PartiallyPaid") && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleResetStatus(milestone)}>
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Reset to Scheduled
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDuplicate(milestone)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => fetchLogs(milestone)}>
                                  <History className="w-4 h-4 mr-2" />
                                  View History
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setDeleteConfirm(milestone)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}

                  {milestones.length === 0 && (
                    <tr className="border-b border-border">
                      <td colSpan={tableColSpan} className="px-3 py-6 text-center text-sm text-muted-foreground">
                        No payment milestones yet
                      </td>
                    </tr>
                  )}

                  {/* The subscription — one row for the whole recurring
                      agreement, expanding into its months. It sits in this
                      table because that's where finance reads the deal, but its
                      figures never join the milestone totals or the contract
                      value above. */}
                  {subscription?.subscription && (
                    <SubscriptionRow
                      programId={programId}
                      data={subscription}
                      isFinance={isFinance}
                      colSpan={tableColSpan}
                      onChanged={(fresh) => (fresh ? setSubscription(fresh) : fetchData())}
                    />
                  )}
                  {isFinance && !subscription?.subscription && (
                    <AddSubscriptionRow
                      programId={programId}
                      suggested={subscription?.suggested_from_quote}
                      colSpan={tableColSpan}
                      onChanged={(fresh) => (fresh ? setSubscription(fresh) : fetchData())}
                    />
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Milestone Dialog */}
      <Dialog open={showAddMilestone} onOpenChange={setShowAddMilestone}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Payment Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={milestoneForm.title}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                placeholder="e.g., Deposit, Progress Payment, Final Balance"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={milestoneForm.type}
                  onValueChange={(v) => setMilestoneForm({ ...milestoneForm, type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Deposit">Deposit</SelectItem>
                    <SelectItem value="Progress">Progress</SelectItem>
                    <SelectItem value="Final">Final</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount Type</Label>
                <Select
                  value={milestoneForm.amount_type}
                  onValueChange={(v) => setMilestoneForm({ ...milestoneForm, amount_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fixed">Fixed Amount</SelectItem>
                    <SelectItem value="PercentOfContract">% of Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>
                {milestoneForm.amount_type === "PercentOfContract" ? "Percentage" : "Amount"}
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  value={milestoneForm.amount_value}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, amount_value: parseFloat(e.target.value) || 0 })}
                  className={milestoneForm.amount_type === "PercentOfContract" ? "pr-8" : "pl-6"}
                />
                {milestoneForm.amount_type === "PercentOfContract" ? (
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                ) : (
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                )}
              </div>
              {milestoneForm.amount_type === "PercentOfContract" && (
                <p className="text-xs text-muted-foreground">
                  = {formatCurrency((milestoneForm.amount_value / 100) * (schedule?.contract_value || 0))}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Due Date Rule</Label>
              <Select
                value={milestoneForm.due_rule_type}
                onValueChange={(v) => setMilestoneForm({ ...milestoneForm, due_rule_type: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ManualDate">Manual Date</SelectItem>
                  <SelectItem value="OnActivation">On Activation</SelectItem>
                  <SelectItem value="OnStartDate">On Start Date</SelectItem>
                  <SelectItem value="RelativeDaysFromStart">Days from Start</SelectItem>
                  <SelectItem value="OnDeliverableCount">On Deliverable Progress %</SelectItem>
                  <SelectItem value="OnMilestoneCompletion">On Final Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {milestoneForm.due_rule_type === "ManualDate" && (
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={milestoneForm.due_date}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                />
              </div>
            )}
            {milestoneForm.due_rule_type === "RelativeDaysFromStart" && (
              <div className="space-y-2">
                <Label>Days after project start</Label>
                <Input
                  type="number"
                  value={milestoneForm.relative_days}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, relative_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
            {milestoneForm.due_rule_type === "OnDeliverableCount" && (
              <div className="space-y-2">
                <Label>Trigger when deliverables are % complete</Label>
                <Input
                  type="number"
                  value={milestoneForm.trigger_threshold}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, trigger_threshold: parseInt(e.target.value) || 0 })}
                  max={100}
                  min={1}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={milestoneForm.notes}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddMilestone(false); resetMilestoneForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleAddMilestone} disabled={saving || !milestoneForm.title}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={showEditMilestone} onOpenChange={setShowEditMilestone}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Payment Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={milestoneForm.title}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={milestoneForm.type}
                  onValueChange={(v) => setMilestoneForm({ ...milestoneForm, type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Deposit">Deposit</SelectItem>
                    <SelectItem value="Progress">Progress</SelectItem>
                    <SelectItem value="Final">Final</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount Type</Label>
                <Select
                  value={milestoneForm.amount_type}
                  onValueChange={(v) => setMilestoneForm({ ...milestoneForm, amount_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fixed">Fixed Amount</SelectItem>
                    <SelectItem value="PercentOfContract">% of Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>
                {milestoneForm.amount_type === "PercentOfContract" ? "Percentage" : "Amount"}
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  value={milestoneForm.amount_value}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, amount_value: parseFloat(e.target.value) || 0 })}
                  className={milestoneForm.amount_type === "PercentOfContract" ? "pr-8" : "pl-6"}
                />
                {milestoneForm.amount_type === "PercentOfContract" ? (
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                ) : (
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date Rule</Label>
              <Select
                value={milestoneForm.due_rule_type}
                onValueChange={(v) => setMilestoneForm({ ...milestoneForm, due_rule_type: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ManualDate">Manual Date</SelectItem>
                  <SelectItem value="OnActivation">On Activation</SelectItem>
                  <SelectItem value="OnStartDate">On Start Date</SelectItem>
                  <SelectItem value="RelativeDaysFromStart">Days from Start</SelectItem>
                  <SelectItem value="OnDeliverableCount">On Deliverable Progress %</SelectItem>
                  <SelectItem value="OnMilestoneCompletion">On Final Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {milestoneForm.due_rule_type === "ManualDate" && (
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={milestoneForm.due_date}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                />
              </div>
            )}
            {milestoneForm.due_rule_type === "RelativeDaysFromStart" && (
              <div className="space-y-2">
                <Label>Days after project start</Label>
                <Input
                  type="number"
                  value={milestoneForm.relative_days}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, relative_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
            {milestoneForm.due_rule_type === "OnDeliverableCount" && (
              <div className="space-y-2">
                <Label>Trigger when deliverables are % complete</Label>
                <Input
                  type="number"
                  value={milestoneForm.trigger_threshold}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, trigger_threshold: parseInt(e.target.value) || 0 })}
                  max={100}
                  min={1}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={milestoneForm.notes}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditMilestone(false); setSelectedMilestone(null); resetMilestoneForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEditMilestone} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={showMarkPaid} onOpenChange={setShowMarkPaid}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {selectedMilestone?.title} - Expected: {formatCurrency(selectedMilestone?.calculated_amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Payment Amount</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={paidForm.paid_amount}
                  onChange={(e) => setPaidForm({ ...paidForm, paid_amount: parseFloat(e.target.value) || 0 })}
                  className="pl-8"
                />
              </div>
              {selectedMilestone?.paid_amount > 0 && (
                <p className="text-xs text-muted-foreground">
                  Previously paid: {formatCurrency(selectedMilestone.paid_amount)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paidForm.paid_date}
                onChange={(e) => setPaidForm({ ...paidForm, paid_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={paidForm.notes}
                onChange={(e) => setPaidForm({ ...paidForm, notes: e.target.value })}
                placeholder="Payment reference, check number, etc."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowMarkPaid(false); setSelectedMilestone(null); }}>
              Cancel
            </Button>
            <Button onClick={handleMarkPaid} disabled={saving || paidForm.paid_amount <= 0}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waive Dialog */}
      <Dialog open={showWaive} onOpenChange={setShowWaive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waive Payment Milestone</DialogTitle>
            <DialogDescription>
              Waiving "{selectedMilestone?.title}" ({formatCurrency(selectedMilestone?.calculated_amount)})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  This action cannot be undone. The milestone will be marked as waived and removed from collection tracking.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason for waiving (required)</Label>
              <Textarea
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
                placeholder="Explain why this payment is being waived..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowWaive(false); setSelectedMilestone(null); setWaiveReason(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleWaive}
              disabled={saving || waiveReason.trim().length < 5}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Waive Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach Invoice Dialog */}
      <Dialog open={showAttachInvoice} onOpenChange={setShowAttachInvoice}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach Invoice</DialogTitle>
            <DialogDescription>
              Link an external invoice to "{selectedMilestone?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Invoice URL</Label>
              <Input
                value={invoiceUrl}
                onChange={(e) => setInvoiceUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice ID (optional)</Label>
              <Input
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                placeholder="INV-001"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAttachInvoice(false); setSelectedMilestone(null); setInvoiceUrl(""); setInvoiceId(""); }}>
              Cancel
            </Button>
            <Button onClick={handleAttachInvoice} disabled={saving || !invoiceUrl.trim()}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Attach Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Schedule Dialog */}
      <Dialog open={showEditSchedule} onOpenChange={setShowEditSchedule}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contract Value</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  value={scheduleForm.contract_value}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, contract_value: parseFloat(e.target.value) || 0 })}
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Changing this will recalculate all percentage-based milestones
              </p>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={scheduleForm.currency}
                onValueChange={(v) => setScheduleForm({ ...scheduleForm, currency: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSchedule(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSchedule} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Milestone History</DialogTitle>
            <DialogDescription>{selectedMilestone?.title}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No history yet</p>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <History className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{log.action_type}</p>
                      <p className="text-xs text-muted-foreground">
                        by {log.actor_name} • {formatDate(log.created_at)}
                      </p>
                      {log.payload_snapshot && (
                        <pre className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">
                          {JSON.stringify(log.payload_snapshot, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Milestone?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"?
              {deleteConfirm?.status === "Paid" || deleteConfirm?.status === "PartiallyPaid" ? (
                <span className="block mt-2 text-amber-600 font-medium">
                  ⚠️ Warning: This milestone has ${deleteConfirm?.paid_amount?.toLocaleString() || 0} recorded as paid.
                </span>
              ) : null}
              <span className="block mt-1">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteMilestone(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Schedule Confirmation */}
      <AlertDialog open={showDeleteSchedule} onOpenChange={setShowDeleteSchedule}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the entire payment schedule for this program?
              {milestones.length > 0 && (
                <span className="block mt-2 font-medium">
                  This will delete <strong>{milestones.length}</strong> milestone{milestones.length > 1 ? 's' : ''}.
                </span>
              )}
              {summary?.total_paid > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  ⚠️ Warning: {formatCurrency(summary.total_paid)} has been marked as paid.
                </span>
              )}
              <span className="block mt-2 text-muted-foreground">
                This action cannot be undone. You can create a new schedule afterwards.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSchedule}
              disabled={saving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-schedule-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Schedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Delivery Dialog (PM action) */}
      <Dialog open={showSubmitDelivery} onOpenChange={setShowSubmitDelivery}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-blue-600" />
              Submit as Delivered to Client
            </DialogTitle>
            <DialogDescription>
              Submit "{selectedMilestone?.title}" to Finance for invoicing. This notifies the finance team that the deliverable is complete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Delivery Note</Label>
              <Textarea
                value={deliveryForm.delivery_note}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_note: e.target.value })}
                placeholder="Describe what was delivered, any relevant details for Finance..."
                rows={3}
                data-testid="delivery-note-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Proof of Delivery URL (optional)</Label>
              <Input
                value={deliveryForm.delivery_proof_url}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_proof_url: e.target.value })}
                placeholder="https://dropbox.com/... or link to deliverable"
                data-testid="delivery-proof-url-input"
              />
              <p className="text-xs text-muted-foreground">Link to Dropbox, Google Drive, or any proof of delivery</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSubmitDelivery(false); setSelectedMilestone(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitDelivery} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-submit-delivery-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <ArrowRight className="w-4 h-4 mr-1" />
              Submit to Finance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return to PM Dialog (Finance action) */}
      <Dialog open={showReturnToPm} onOpenChange={setShowReturnToPm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-orange-600" />
              Return to PM
            </DialogTitle>
            <DialogDescription>
              Return "{selectedMilestone?.title}" to the PM for corrections or additional information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason for returning (required)</Label>
              <Textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Explain what needs to be corrected or updated..."
                rows={3}
                data-testid="return-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReturnToPm(false); setSelectedMilestone(null); setReturnReason(""); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReturnToPm} 
              disabled={saving || returnReason.trim().length < 3}
              data-testid="confirm-return-to-pm-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Return to PM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Invoice Sent Dialog (Finance action) */}
      {/* Renders itself away for anyone who isn't finance, or any project with
          no payment schedule. */}
      <TrackerCollectionsCard programId={programId} />

      <Dialog open={showMarkInvoiceSent} onOpenChange={setShowMarkInvoiceSent}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Mark Invoice Sent
            </DialogTitle>
            <DialogDescription>
              Mark "{selectedMilestone?.title}" as invoice sent to client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Invoice URL (optional)</Label>
              <Input
                value={invoiceSentForm.invoice_url}
                onChange={(e) => setInvoiceSentForm({ ...invoiceSentForm, invoice_url: e.target.value })}
                placeholder="https://..."
                data-testid="invoice-sent-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Invoice ID (optional)</Label>
              <Input
                value={invoiceSentForm.invoice_id}
                onChange={(e) => setInvoiceSentForm({ ...invoiceSentForm, invoice_id: e.target.value })}
                placeholder="INV-001"
                data-testid="invoice-sent-id-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowMarkInvoiceSent(false); setSelectedMilestone(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkInvoiceSent} 
              disabled={saving}
              data-testid="confirm-mark-invoice-sent-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm Invoice Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MilestonePaymentsTab;
